import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './Legal/Components/LegalContent.css';
import RichTextEditor from './ContentManagement/RichTextEditor';
import LegalService from './Legal/Service';
import './ContentManagement/ContentManagement.css'
import './ContentManagement/RichTextEditor.css';

const LegalManager = () => {
  const [activeTab, setActiveTab] = useState('terms');
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [helpContent, setHelpContent] = useState('');
  const [consentContent, setConsentContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      const [termsResponse, privacyResponse, helpResponse, consentResponse] = await Promise.all([
        LegalService.getTermsAndConditions(),
        LegalService.getPrivacyPolicy(),
        LegalService.getHelpSupport(),
        LegalService.getConsent()
      ]);

      setTermsContent(termsResponse.data?.data?.content || termsResponse.data?.content || '');
      setPrivacyContent(privacyResponse.data?.data?.content || privacyResponse.data?.content || '');
      setHelpContent(helpResponse.data?.data?.content || helpResponse.data?.content || '');
      setConsentContent(consentResponse.data?.data?.content || consentResponse.data?.content || '');
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load legal content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (value) => {
    if (activeTab === 'terms') {
      setTermsContent(value);
    } else if (activeTab === 'privacy') {
      setPrivacyContent(value);
    } else if (activeTab === 'help') {
      setHelpContent(value);
    } else if (activeTab === 'consent') {
      setConsentContent(value);
    }
    setHasUnsavedChanges(true);
  };

  const handleEditToggle = () => {
    if (isEditing && hasUnsavedChanges) {
      const confirmSave = window.confirm('You have unsaved changes. Do you want to save before switching to view mode?');
      if (confirmSave) {
        handleSave();
      }
    }
    setIsEditing(!isEditing);
  };

  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges && isEditing) {
      const confirmSwitch = window.confirm('You have unsaved changes. Do you want to save before switching tabs?');
      if (confirmSwitch) {
        handleSave();
      }
    }
    setActiveTab(newTab);
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = activeTab === 'terms' ? termsContent : activeTab === 'privacy' ? privacyContent : activeTab === 'help' ? helpContent : consentContent;
      const type = activeTab === 'terms' ? 'terms_conditions' : activeTab === 'privacy' ? 'privacy_policy' : activeTab === 'help' ? 'help_support' : 'consent';
      const title = activeTab === 'terms' ? 'Terms and Conditions' : activeTab === 'privacy' ? 'Privacy Policy' : activeTab === 'help' ? 'Help and Support' : 'Consent';

      await LegalService.createOrUpdateContent({
        type,
        title,
        content
      });

      setHasUnsavedChanges(false);
      setIsEditing(false);
      const tabName = activeTab === 'terms' ? 'Terms & Conditions' : activeTab === 'privacy' ? 'Privacy Policy' : activeTab === 'help' ? 'Help & Support' : 'Consent';
      toast.success(`${tabName} saved successfully!`);
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading content...</p>
      </div>
    );
  }

  return (
    <div className="content-management">
      <div className="content-tabs">
        <button
          className={`tab-button ${activeTab === 'terms' ? 'active' : ''}`}
          onClick={() => !loading && handleTabChange('terms')}
          disabled={loading}
        >
          Terms & Conditions
        </button>
        <button
          className={`tab-button ${activeTab === 'privacy' ? 'active' : ''}`}
          onClick={() => !loading && handleTabChange('privacy')}
          disabled={loading}
        >
          Privacy Policy
        </button>
        <button
          className={`tab-button ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => !loading && handleTabChange('help')}
          disabled={loading}
        >
          Help & Support
        </button>
        <button
          className={`tab-button ${activeTab === 'consent' ? 'active' : ''}`}
          onClick={() => !loading && handleTabChange('consent')}
          disabled={loading}
        >
          Consent
        </button>
      </div>

      <div className="content-editor-section">
        <div className="editor-header">
          <h3>
            {activeTab === 'terms'
              ? 'Terms & Conditions'
              : activeTab === 'privacy'
              ? 'Privacy Policy'
              : activeTab === 'help'
              ? 'Help & Support'
              : 'Consent'}
          </h3>
          <div className="editor-actions">
            {!isEditing ? (
              <></>
            ) : (
              <div className="editing-actions">
                <button
                  className="cancel-button"
                  onClick={handleEditToggle}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="save-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="container-fluid">
          <div className="editor-preview-container row">
            <div className="editor-container col-12 mb-3">
              <div className="editor-header-section">
                <h4>Content Editor</h4>
                {!isEditing && (
                  <button
                    className="edit-content-button"
                    onClick={handleEditToggle}
                    disabled={loading}
                  >
                    Edit Content
                  </button>
                )}
              </div>

              <div className="editor-wrapper">
                <RichTextEditor
                  value={
                    activeTab === 'terms'
                      ? termsContent
                      : activeTab === 'privacy'
                      ? privacyContent
                      : activeTab === 'help'
                      ? helpContent
                      : consentContent
                  }
                  onChange={handleContentChange}
                  placeholder={`Enter ${
                    activeTab === 'terms'
                      ? 'Terms & Conditions'
                      : activeTab === 'privacy'
                      ? 'Privacy Policy'
                      : activeTab === 'help'
                      ? 'Help & Support'
                      : 'Consent'
                  } content here...`}
                  height="400px"
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="content-preview col-12">
              <h4>Live Preview</h4>
              <div
                className="preview-content"
                dangerouslySetInnerHTML={{
                  __html:
                    activeTab === 'terms'
                      ? termsContent
                      : activeTab === 'privacy'
                      ? privacyContent
                      : activeTab === 'help'
                      ? helpContent
                      : consentContent
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalManager;
